import { Injectable, inject } from "@angular/core";
import { HttpService } from "@app/core/services/http/http.service";
import { Observable, of, from, tap, map, exhaustMap, switchMap, concatMap, catchError } from "rxjs";
import {
  Credentials,
  Authentication,
  AuthenticationAdapter,
  AssumeIdentityAdapter,
  AssumeIdentity,
  PortalsPlusJsAuth,
  PortalsPlusJsAuthAdapter
} from "@app/shared/models/authentication.model";
import { APP_WS, IPLUS_URL, NODEJS_URL } from "@app/shared/constants/url.constants";
import { ACCOUNTS_KEY } from "@app/shared/constants/storage-keys.constants";
import { LoginContext } from "@app/app-modules/login/login.model";
import { StorageDataService } from "@app/core/services/storage-data.service";
import { AvatarEnvironment, AvatarEnvironmentAdapter, Environment, EnvironmentAdapter } from "@app/shared/models/environment.model";
import { FileDownloadService } from "@app/core/services/file-download.service";
import { FileDownload } from "@app/shared/models/file.model";
import { cloneDeep } from "lodash-es";
import { FileTypes } from "@app/shared/constants/file-types.constants";

/**
 * Authentication workflow
 */
@Injectable({
  providedIn: "root"
})
export class AuthenticationService {
  private http = inject(HttpService);
  private fileDownloadService = inject(FileDownloadService);

  private _passwordReset: boolean;
  private _accountHandler: AccountHandler;

  constructor() {
    this._accountHandler = new AccountHandler();
    this.loadCachedAccounts();
  }

  /**
   * Load cached accounts during application load or refresh
   */
  private loadCachedAccounts() {
    const cachedAccounts = this._accountHandler?.getAccounts();
    let account: Authentication;
    if (cachedAccounts.length === 0) {
      return;
    }

    // Set avatar to isLoading to trigger animation;
    cachedAccounts.some((x) => {
      if (!x?.avatar) {
        x._isLoadingAvatar = true;
      }
    });

    from(cachedAccounts)
      .pipe(
        concatMap((results) => {
          account = results;

          return this.fetchAvatarAndEnvironments(results.credentials.username).pipe(
            switchMap((result) => {
              if (!result?.avatar) {
                return of("");
              }

              const payload: FileDownload = {
                file: result.avatar,
                type: FileTypes.PNG
              };
              return this.fileDownloadService.downloadFileAsBase64Url(payload, undefined, true);
            })
          );
        })
      )
      .subscribe((results) => {
        account.avatar = results;
        account._isLoadingAvatar = false;
      });
  }

  /**
   * Fetch user's environment list
   * @param userid
   */
  fetchEnvironments(userid: string): Observable<Environment[]> {
    const params = { userid };
    const envAdapter: EnvironmentAdapter = new EnvironmentAdapter();
    return this.http
      .removeDefaultHeaders()
      .addDataParams(params)
      .get(IPLUS_URL.ENVIRONMENT_LIST, { params })
      .pipe(map((response: any) => envAdapter.adapt(response)));
  }

  /**
   * Fetch user's avatar image and environment list
   * @param userid
   */
  fetchAvatarAndEnvironments(userid: string): Observable<AvatarEnvironment> {
    const params = { userid };
    const envAdapter: AvatarEnvironmentAdapter = new AvatarEnvironmentAdapter();
    return this.http
      .removeDefaultHeaders()
      .addDataParams(params)
      .get<AvatarEnvironment>(IPLUS_URL.ENVIRONMENT_LIST, { params })
      .pipe(map((response: any) => envAdapter.adapt(response)));
  }

  /**
   * Authenticates the user.
   * @param context The login parameters.
   * @param addAccount True, when login successful add to account array
   * @return The user credentials.
   */
  login(context: LoginContext, addAccount: boolean): Observable<Authentication> {
    const params = {
      username: context.username,
      password: context.password,
      environmentId: context.environmentId
    };

    const authAdapter: AuthenticationAdapter = new AuthenticationAdapter();

    // Prevent multiple requests
    return of("").pipe(
      exhaustMap(() => {
        return this.http
          .removeDefaultHeaders()
          .addDataParams(params)
          .get<any>(IPLUS_URL.AUTHENTICATE_USER, { params })
          .pipe(
            map((response: any[]) => authAdapter.adapt(response)),
            tap((authentication) => {
              authentication.avatar = context.avatar;
              authentication.rememberMe = context.rememberMe || false;
              if (addAccount) {
                this.addAccount(authentication);
              }
            })
          );
      })
    );
  }

  /**
   * Secondary login used to access locked menu items
   * @param password
   */
  secondaryLogin(password: string): Observable<boolean> {
    const params = {
      username: this.credentials.username,
      password: password
    };

    // Prevent multiple requests
    return of("").pipe(
      exhaustMap(() => {
        return this.http
          .addDataParams(params)
          .get<any>(IPLUS_URL.AUTHENTICATE_PASS, { params })
          .pipe(map((response) => !!response.username));
      })
    );
  }

  /**
   * Used when a house account is selecting a profile to use during
   * the log in process
   */
  assumeIdentityLogin(user: string) {
    const identityAdapter: AssumeIdentityAdapter = new AssumeIdentityAdapter();
    // Prevent multiple requests
    return of("").pipe(
      exhaustMap(() => {
        return this.http
          .addDataParams({ username: user })
          .get<AssumeIdentity>(IPLUS_URL.ASSUME_IDENTITY)
          .pipe(map((response: any) => identityAdapter.adapt(response)));
      })
    );
  }

  /**
   * Authenticate with PortalsPlusJs and get token to use with API.
   * @param username
   * @param password
   * @returns
   */
  nodeJsGetToken(username: string, password: string, addAccount?: boolean) {
    const responseAdapter: PortalsPlusJsAuthAdapter = new PortalsPlusJsAuthAdapter();
    return this.http
      .removeDefaultHeaders()
      .removeDefaultEncoding()
      .makeNonUSSICall()
      .addUrlPrefix()
      .post<PortalsPlusJsAuth>(NODEJS_URL.GET_TOKEN, { username, password, portal: APP_WS.iplus })
      .pipe(
        map((response: any) => responseAdapter.adapt(response)),
        tap((resp) => {
          if (resp) {
            if (!addAccount) {
              this.activeAccount.credentials.nodeJsAuthentication = {
                accessToken: resp?.accessToken,
                refreshToken: resp?.refreshToken
              };
              this.updateStorage();
            }
          }
        }),
        catchError(() => of(undefined))
      );
  }

  /**
   * Re-Authenticate with NodeJS with refresh token
   * @param username
   * @param password
   * @returns
   */
  nodeJsRefreshToken() {
    const responseAdapter: PortalsPlusJsAuthAdapter = new PortalsPlusJsAuthAdapter();
    return this.http
      .removeDefaultHeaders()
      .removeDefaultEncoding()
      .makeNonUSSICall()
      .addUrlPrefix()
      .addNodeJsAuth(true)
      .post<PortalsPlusJsAuth>(NODEJS_URL.REFRESH_TOKEN, this.activeAccount.credentials.nodeJsAuthentication)
      .pipe(
        map((response: any) => responseAdapter.adapt(response)),
        tap((resp) => {
          if (resp) {
            this.activeAccount.credentials.nodeJsAuthentication.accessToken = resp?.accessToken;
            this.updateStorage();
          }
        })
      );
  }

  /**
   * Validate access token
   * @returns
   */
  nodeJsValidateToken() {
    return this.http
      .removeDefaultHeaders()
      .removeDefaultEncoding()
      .makeNonUSSICall()
      .addUrlPrefix()
      .addNodeJsAuth()
      .post<PortalsPlusJsAuth>(NODEJS_URL.VALIDATE_TOKEN, this.activeAccount.credentials.nodeJsAuthentication);
  }

  /**
   * Validates the current session if the user has `remember me` active.
   */
  validateSession() {
    if (this.hasCredentialsPersisted()) {
      return this.http.get<any>(IPLUS_URL.VALIDATE_SESSION);
    } else {
      return of("");
    }
  }

  /**
   * Checks is the user is authenticated.
   * @return True if the user is authenticated.
   */
  isAuthenticated = (): boolean => {
    const data = this._accountHandler.getActiveAccount();
    return data ? !!this._accountHandler.getActiveAccount().token : false;
  };

  /**
   * Check if the user has 2FA enabled, and make sure they are verified
   * @returns
   */
  is2faAuthenticated(): boolean {
    const data = this._accountHandler.getActiveAccount();
    return data?.twoFactorSettings?.key2FA ? data.credentials.authenticated2FA : true;
  }

  /**
   * Checks if the password is expired
   * @return True if the password is expired
   */
  isPasswordExpired(): boolean {
    const data = this._accountHandler.getActiveAccount();
    return data ? !!this._accountHandler.getActiveAccount().isPasswordExpired : false;
  }

  /**
   * Set a storage value stating we have an expired password
   * and toggle on the child component for change-password in the html
   */
  setPasswordExpired(): void {
    if (this._accountHandler.getActiveAccount()) {
      this._accountHandler.getActiveAccount().isPasswordExpired = true;
    }
  }

  /**
   * Determine if we have bad or corrupted data
   */
  hasBadStorageData(): boolean {
    if (!this.credentials) {
      return true;
    }
    if (this.isAuthenticated()) {
      if (this.credentials) {
        if (this.credentials.username && this.credentials.username === "") {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Check if accounts have been persisted
   */
  hasCredentialsPersisted(): boolean {
    return this._accountHandler.hasCredentialsPersisted();
  }

  /**
   * Clears persisted credentials.
   */
  clearPersistedCredentials(): void {
    this._accountHandler.clearAccounts(true);
  }

  /**
   *  Clear Credentials from session storage
   */
  clearCredentials(): void {
    this._accountHandler.clearAccounts(false);
  }

  /**
   * Update browser storage with accounts.
   */
  updateStorage(): void {
    this._accountHandler.updateStorage();
  }

  /**
   * Swap currently active account with the account that the user selected and refresh page.
   */
  swapActiveAccount(account: Authentication): void {
    this._accountHandler.setActiveAccount(account);
  }

  /**
   * Add new account to accounts list and set it as active.
   * @param account
   */
  addAccount(account: Authentication): void {
    this._accountHandler.addAccount(account);
  }

  /**
   * Remove selected account from list and update storage token values
   * @param account
   */
  removeAccount(account: Authentication) {
    this._accountHandler.deleteAccount(account);
  }

  /**
   * Check array if the menu option id exists
   * @param id
   * @returns
   */
  checkDontShowAgainId(id: string): boolean {
    if (!this.dontShowAgainIds?.length) {
      return false;
    }

    const exists: string = this.dontShowAgainIds.find((x) => x === id);
    return exists !== undefined;
  }

  /**
   * Add an id for a menu that will not show modals on exit.
   * @param id
   */
  addDontShowAgainId(id: string) {
    if (this._accountHandler.getActiveAccount()) {
      this.dontShowAgainIds.push(id);
      this.updateStorage();
    }
  }

  get passwordReset(): boolean {
    return this._passwordReset;
  }

  set passwordReset(reset: boolean) {
    this._passwordReset = reset;
  }

  /**
   * Is user an application administrator
   */
  get isAdmin(): boolean {
    if (this.credentials) {
      return this.credentials.isAdmin;
    }
    return false;
  }

  /**
   * Is user a system administrator
   */
  get isSystemAdmin(): boolean {
    if (this.credentials) {
      return this.credentials.isAdmin && this.credentials.isSuper;
    }
    return false;
  }

  /**
   * Get the user's credentials.
   * @return The user credentials or a default object if the user is not authenticated.
   */
  get credentials(): Credentials {
    const credentials: Credentials = {
      isAdmin: false,
      isSuper: false,
      isHouseAccount: false,
      isIdentityAssumed: false,
      displayName: "",
      username: "",
      authenticated2FA: false,
      nodeJsAuthentication: {
        accessToken: "",
        refreshToken: ""
      }
    };

    if (this._accountHandler.getActiveAccount()) {
      const userCredentials: Credentials = this._accountHandler.getActiveAccount()?.credentials;
      credentials.isAdmin = userCredentials?.isAdmin ?? false;
      credentials.isSuper = userCredentials?.isSuper;
      credentials.isHouseAccount = userCredentials?.isHouseAccount;
      credentials.isIdentityAssumed = userCredentials?.isIdentityAssumed;
      credentials.displayName = userCredentials?.displayName;
      credentials.username = userCredentials?.username;
      credentials.authenticated2FA = userCredentials?.authenticated2FA;
      credentials.nodeJsAuthentication = {
        accessToken: userCredentials?.nodeJsAuthentication?.accessToken,
        refreshToken: userCredentials?.nodeJsAuthentication?.refreshToken
      };
    }

    return credentials;
  }

  set displayName(name: string) {
    if (!this.isAuthenticated()) {
      return;
    }
    this._accountHandler.getActiveAccount().credentials.displayName = name;
  }

  /**
   * Gets the user environment.
   */
  get environment(): Environment | null {
    if (!this._accountHandler.hasCredentialsPersisted()) {
      if (!this.isAuthenticated()) {
        return null;
      }
    }
    const environment = this._accountHandler.getActiveAccount().environment;
    if (environment) {
      return environment;
    }
    return null;
  }

  /**
   * Get token value
   */
  get token(): string | null {
    if (!this.isAuthenticated()) {
      return null;
    }
    let token = "";
    if (this._accountHandler.getActiveAccount()) {
      token = this._accountHandler.getActiveAccount().token;
    }
    return token;
  }

  /**
   * Set token value
   */
  set token(value: string) {
    if (this._accountHandler.getActiveAccount()) {
      this._accountHandler.getActiveAccount().token = value;
    }
  }

  /**
   * Get user's avatar
   * Avatar value is stored as a base64 string
   */
  get avatar(): string {
    let avatar = "";
    if (this._accountHandler.getActiveAccount()) {
      avatar = this._accountHandler.getActiveAccount().avatar;
    }
    return avatar;
  }

  /**
   * Set user's avatar
   * Avatar value is retrieved as a base64 string
   */
  set avatar(url: string) {
    if (this._accountHandler.getActiveAccount()) {
      this._accountHandler.getActiveAccount().avatar = url;
    }
  }

  set lastLogin(lastLogin: Date) {
    if (this._accountHandler.getActiveAccount()) {
      this._accountHandler.getActiveAccount().lastLogin = lastLogin;
    }
  }

  get lastLogin() {
    if (!this.isAuthenticated()) {
      return null;
    }
    const lastLogin = this._accountHandler.getActiveAccount().lastLogin;
    if (lastLogin) {
      return lastLogin as Date;
    }
    return null;
  }

  set emailAddress(emailAddress: string) {
    if (this._accountHandler.getActiveAccount()) {
      this._accountHandler.getActiveAccount().emailAddress = emailAddress;
    }
  }

  get emailAddress() {
    if (!this.isAuthenticated()) {
      return null;
    }
    const emailAddress = this._accountHandler.getActiveAccount().emailAddress;
    if (emailAddress) {
      return emailAddress;
    }
    return null;
  }

  get activeAccount() {
    return this._accountHandler.getActiveAccount();
  }

  /**
   * Updates the stored values for isHouseAccount
   */
  set isHouseAccount(isHouse: boolean) {
    if (this._accountHandler.getActiveAccount()) {
      this._accountHandler.getActiveAccount().credentials.isHouseAccount = isHouse;
    }
  }

  /**
   * Return if the account is a house account
   */
  get isHouseAccount(): boolean {
    if (this.credentials) {
      return this.credentials.isHouseAccount;
    }
    return false;
  }

  /**
   * Updates the stored values for isIdentityAssumed
   */
  set isIdentityAssumed(isAssumed: boolean) {
    if (this._accountHandler.getActiveAccount()) {
      this._accountHandler.getActiveAccount().credentials.isIdentityAssumed = isAssumed;
    }
  }

  /**
   * Return if the account is a house account
   */
  get isIdentityAssumed(): boolean {
    if (!this.isAuthenticated()) {
      return false;
    }
    const isIdentityAssumed = this._accountHandler.getActiveAccount()?.credentials?.isIdentityAssumed;
    if (isIdentityAssumed) {
      return isIdentityAssumed;
    }
    return false;
  }

  /**
   * Updates the stored values for _isLoadingAvatar
   * This value is set during shell initialization
   */
  set isLoadingAvatar(isLoading: boolean) {
    if (this._accountHandler.getActiveAccount()) {
      this._accountHandler.getActiveAccount()._isLoadingAvatar = isLoading;
    }
  }

  /**
   * Return if the account is loading avatar
   */
  get isLoadingAvatar(): boolean {
    if (!this.isAuthenticated()) {
      return false;
    }
    return this._accountHandler.getActiveAccount()?._isLoadingAvatar;
  }

  /**
   * Updates the stored values for secret 2fa
   * This value is set during portalLogin()
   */
  set key2FA(key2FA: string) {
    if (this._accountHandler.getActiveAccount()) {
      this._accountHandler.getActiveAccount().twoFactorSettings.key2FA = key2FA;
    }
  }

  /**
   * Return 2fa secret
   */
  get key2FA(): string {
    if (!this.isAuthenticated()) {
      return "";
    }

    return this._accountHandler.getActiveAccount()?.twoFactorSettings.key2FA;
  }

  /**
   * Updates the stored values for 2fa type
   */
  set type2FA(type2FA: number) {
    if (this._accountHandler.getActiveAccount()) {
      this._accountHandler.getActiveAccount().twoFactorSettings.type2FA = type2FA;
    }
  }

  /**
   * Return 2fa type
   */
  get type2FA(): number {
    if (!this.isAuthenticated()) {
      return null;
    }

    return this._accountHandler.getActiveAccount()?.twoFactorSettings.type2FA;
  }

  /**
   * Updates the stored value for 2fa bypass
   */
  set byPass2FA(byPass2FA: boolean) {
    if (this._accountHandler.getActiveAccount()) {
      this._accountHandler.getActiveAccount().twoFactorSettings.byPass2FA = byPass2FA;
    }
  }

  /**
   * Return 2fa bypass value
   */
  get byPass2FA(): boolean {
    if (!this.isAuthenticated()) {
      return null;
    }

    return this._accountHandler.getActiveAccount()?.twoFactorSettings.byPass2FA;
  }

  /**
   * Set account as valid authentication when 2fa is active
   */
  set authenticated2FA(value: boolean) {
    if (this._accountHandler.getActiveAccount()) {
      this._accountHandler.getActiveAccount().credentials.authenticated2FA = value;
      this.updateStorage();
    }
  }

  /**
   * Return a list of all accounts that are not active
   */
  get getAccounts(): Authentication[] {
    return this._accountHandler.getAccounts();
  }

  /**
   * Return a list of all accounts that are not active
   */
  get additionalAccounts(): Authentication[] {
    return this._accountHandler.getAdditionalAccounts();
  }

  get houseAccount(): Authentication {
    return this.additionalAccounts.find((x) => x.credentials.isHouseAccount);
  }

  private get dontShowAgainIds(): string[] {
    return this._accountHandler.getActiveAccount()?.dontShowAgainIds ?? [];
  }
}

/**
 *
 * Class: AccountHandler()
 *
 * Encapsulate multi account handling functionality
 *
 */
class AccountHandler {
  private _activeAccount: Authentication;
  private _accounts: Authentication[] = [];
  private _sd: StorageDataService = new StorageDataService();

  constructor() {}

  /**
   * Add new account
   * @param account
   */
  public addAccount(account: Authentication): void {
    if (account.credentials && account.token) {
      const a = this._accounts.filter((a) => a.credentials.username === account.credentials.username)[0];
      if (a) {
        this.updateAccount(account);
      } else {
        this._accounts.push(account);
      }
      this.setActiveAccount(account);
      this.updateStorage();
    }
  }

  /**
   * Delete an account
   * @param account
   */
  public deleteAccount(account: Authentication): void {
    if (account.isActive) {
      this._resetActiveAccount();
    }

    this._accounts = this._accounts.filter((a) => a.credentials.username !== account.credentials.username);
    // Default to first valid account in the list
    if (!this._activeAccount?.token) {
      this.setActiveAccount(this._accounts[0]);
    }
    this.updateStorage();
  }

  /**
   * Update account properties
   * @param account
   */
  private updateAccount(account: Authentication): void {
    const i: number = this._accounts.map((a) => a.credentials.username).indexOf(account.credentials.username);
    if (i >= 0) {
      this._accounts[i] = cloneDeep(account);
    }
  }

  /**
   * Get active account
   */
  public getActiveAccount(): Authentication {
    if (this._activeAccount) {
      return this._activeAccount;
    }
    if (this._accounts && this._accounts.length > 0) {
      this._activeAccount = this._accounts.filter((a) => a.isActive === true)[0];
      if (!this._activeAccount) {
        this._accounts[0].isActive = true;
        this._activeAccount = this._accounts[0];
      }
    }
    return this._activeAccount;
  }

  /**
   * Set active account
   * @param account
   */
  public setActiveAccount(account: Authentication): void {
    if (this._activeAccount) {
      this._activeAccount.isActive = false;
    }
    const newActive = this._accounts.find((a) => a.credentials.username === account.credentials.username);
    if (newActive) {
      this._activeAccount = newActive;
      this._activeAccount.isActive = true;
    }
    this.updateStorage();
  }

  /**
   * Get an array of cached accounts
   * If internal array is empty, then load from storage
   */
  public getAccounts(): Authentication[] {
    if (this._accounts.length > 0) {
      return this._accounts;
    }
    const accounts = JSON.parse(this._sd.getStorageItem(ACCOUNTS_KEY));
    this._accounts = accounts ? accounts : [];
    return this._accounts;
  }

  /**
   * Get an array of accounts and do not include the active account
   */
  public getAdditionalAccounts(): Authentication[] {
    return this._accounts.filter((a) => a.isActive === false);
  }

  /**
   * Clear account information from storage
   *
   * Data will be persisted if the "rememberMe" flag is true.
   * Persisted data will be cleared when the "rememberMe" flag is false.
   *
   * @param force : if true, clear storage data even if data is persisted
   */
  public clearAccounts(force: boolean = false): void {
    if (this.hasCredentialsPersisted() && !force) {
      this._activeAccount.token = null;
      this.updateStorage();
    } else {
      this._accounts = [];
      this._resetActiveAccount();
      this._sd.removeLocalStorageItem(ACCOUNTS_KEY);
      this._sd.removeAllSessionStorageData();
    }
  }

  /**
   * Update browser storage with accounts.
   */
  public updateStorage(): void {
    const accounts: Authentication[] = cloneDeep(this._accounts);
    let rememberMe: boolean = false;

    accounts.forEach((e) => {
      e.avatar = null;
      if (e.rememberMe) {
        rememberMe = true;
      }
    });

    if (this.hasCredentialsPersisted() || rememberMe) {
      this._sd.setLocalStorageItem(ACCOUNTS_KEY, JSON.stringify(accounts));
    } else {
      this._sd.setSessionStorageItem(ACCOUNTS_KEY, JSON.stringify(accounts));
    }
  }

  /**
   * Check if accounts have been persisted
   * @return True if user credentials are stored in local storage, false otherwise
   */
  public hasCredentialsPersisted(): boolean {
    return this._activeAccount?.rememberMe;
  }

  /**
   * Clear currently active account.
   */
  private _resetActiveAccount() {
    this._activeAccount = {} as Authentication;
  }
}
